import { useEffect, useId, useState } from "react";
import {
	dayDirectionsSegments,
	googleMapsDirectionsUrl,
} from "@japangolmok/content";
import { jpyToKrw, type TripDay, type TripWorkspace } from "@japangolmok/core";
import type { PersistTrip } from "./TripExperience";
import {
	getProfile,
	saveProfile,
	mealChoices,
	type DeviceProfile,
} from "../storage/profile";
import { BottomSheet } from "../ui/BottomSheet";
import { ExternalLink } from "../ui/Shell";
export function DayMap({ day }: { day: TripDay }) {
	const links = dayDirectionsSegments(day.items.filter((i) => !i.deferred));
	const [open, setOpen] = useState(false);
	const panelId = useId();
	return (
		<div className="day-control">
			<button type="button" className="button secondary" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(!open)}><span aria-hidden="true">⌖</span> 이 날 동선 지도로</button>
			<div id={panelId} hidden={!open}>
			<p>연결되면 지도 앱에서 열려요. 위치를 수집하지 않아요.</p>
			<ol>
				{day.items
					.filter((i) => !i.deferred)
					.map((i) => (
						<li key={i.id}>{i.title}</li>
					))}
			</ol>
			{links.map((l) => (
				<ExternalLink key={l.label} href={l.url} className="button secondary">
					{l.label}
				</ExternalLink>
			))}
			</div>
		</div>
	);
}
export function TodayTools({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [open, setOpen] = useState(false),
		[mealOpen, setMealOpen] = useState(false),
		[profile, setProfile] = useState<DeviceProfile | null>(null),
		[error, setError] = useState(""),
		[yen, setYen] = useState("3280"),
		[rate, setRate] = useState(String(trip.krwPer100Jpy ?? ""));
	useEffect(() => {
		getProfile()
			.then(setProfile)
			.catch(() =>
				setError("식사 카드 설정을 읽지 못했어요. 저장 공간을 확인해 주세요."),
			);
	}, []);
	const stay = trip.stay?.addressJa || trip.travelContext?.stayArea;
	const converted = jpyToKrw(Number(yen), trip.krwPer100Jpy);
	return (
		<section className="section today-tools">
			<div className="button-row">
				{stay ? (
					<ExternalLink
						className="button primary"
						href={googleMapsDirectionsUrl(stay)}
					>
						숙소로
					</ExternalLink>
				) : (
					<a className="button secondary" href={`/trips/${trip.id}#itinerary`}>
						숙소 주소 적기
					</a>
				)}
				<button
					type="button"
					className="button secondary"
					disabled={!trip.stay?.addressJa}
					onClick={() => setOpen(true)}
				>
					숙소 주소 보여 주기
				</button>
				<button
					type="button"
					className="button secondary"
					onClick={() => setMealOpen(true)}
				>
					내 식사 카드
				</button>
			</div>
			{open && (
				<BottomSheet
					title={trip.stay?.name || "숙소 주소"}
					onClose={() => setOpen(false)}
				>
					<p className="large-japanese" lang="ja">
						{trip.stay?.addressJa}
					</p>
				</BottomSheet>
			)}
			{mealOpen && (
				<BottomSheet title="내 식사 카드" onClose={() => setMealOpen(false)}>
					<p>이 기기에만 저장해요. 공유·요약 카드에는 담지 않아요.</p>
					{profile && (
						<>
							{mealChoices.map(([id, ko]) => (
								<label key={id}>
									<input
										type="checkbox"
										checked={profile.meals.includes(id)}
										onChange={async (e) => {
											const next = {
												...profile,
												meals: e.target.checked
													? [...profile.meals, id]
													: profile.meals.filter((k) => k !== id),
											};
											try {
												await saveProfile(next);
												setProfile(next);
											} catch {
												setError("설정을 저장하지 못했어요. 다시 골라 주세요.");
											}
										}}
									/>
									{ko}
								</label>
							))}
							<p>고른 음식이 없으면 위에서 골라 주세요.</p>
							{profile.meals.length > 0 && (
								<>
									<p className="large-japanese" lang="ja">
										私は以下の食品を食べられません。
										<br />
										{mealChoices
											.filter((c) => profile.meals.includes(c[0]))
											.map((c) => c[2])
											.join("・")}
										<br />
										これらを使っていない料理はありますか？
									</p>
									<p>
										저는 다음 식품을 먹을 수 없어요. 이 재료를 쓰지 않은 요리가
										있나요?
									</p>
								</>
							)}
						</>
					)}
					<p>
						의료 판단이 아니에요. 심한 알레르기는 의사 소견서를 함께 보여
						주세요. 조리 중 섞임과 소스 재료를 직원에게 직접 확인해 주세요.
					</p>
					<ExternalLink href="https://www.mhlw.go.jp/content/10905000/001704059.pdf">
						후생노동성·소비자청 식품 표시 자료
					</ExternalLink>
				</BottomSheet>
			)}
			<details className="workspace-disclosure">
				<summary>¥→₩ · 내 환율로 계산</summary>
				<p>자동 시세가 아니에요. 환율을 적어 두면 인터넷 없이 계산돼요.</p>
				<label>
					엔화 금액
					<input
						inputMode="decimal"
						type="number"
						min={0}
						value={yen}
						onChange={(e) => setYen(e.target.value)}
					/>
				</label>
				{trip.krwPer100Jpy ? (
					<p role="status">
						약 {converted?.toLocaleString()}원 · 100엔당 {trip.krwPer100Jpy}원
					</p>
				) : (
					<>
						<label>
							100엔당 원화
							<input
								type="number"
								min={100}
								max={10000}
								value={rate}
								onChange={(e) => setRate(e.target.value)}
							/>
						</label>
						<button
							type="button"
							className="button secondary"
							onClick={() =>
								void persist((t) => {
									t.krwPer100Jpy = Number(rate);
								})
							}
						>
							내 환율 저장
						</button>
					</>
				)}
			</details>
			{profile && (
				<label>
					<input
						type="checkbox"
						checked={profile.sound}
						onChange={async (e) => {
							const next = { ...profile, sound: e.target.checked };
							try {
								await saveProfile(next);
								setProfile(next);
							} catch {
								setError("소리 설정을 저장하지 못했어요.");
							}
						}}
					/>{" "}
					완료 소리 켜기 · 기본 꺼짐
				</label>
			)}
			{error && <p role="alert">{error}</p>}
		</section>
	);
}
